import classNames from "classnames";
import { useState } from "react";

import { LoadingDots } from "../loading_dots";
import { Tile } from "../tile";
import css from "./file_upload.module.scss";
import { EmptyContent, EmptyContentProps } from "./parts/empty_content";
import { ErrorContent } from "./parts/error_content";
import { UploadedContent, UploadedContentProps } from "./parts/uploaded_content";
import { useUploadFile } from "./use_upload_file";

function serializeFormData(obj: object, prefix = "") {
  const formData = new FormData();

  const buildFormData = (data: any, parentKey: string) => {
    if (typeof data === "object" && data !== null) {
      Object.keys(data).forEach((key) => {
        const value = data[key];
        const formKey = parentKey ? `${parentKey}[${key}]` : key;
        buildFormData(value, formKey);
      });
    } else {
      formData.append(parentKey, data);
    }
  };

  buildFormData(obj, prefix);
  return formData;
}
type FileUploadProps<RESPONSE_TYPE extends object> = EmptyContentProps &
  Omit<UploadedContentProps, "uploadedFile"> & {
    className?: string;
    url: string;
    name: string;
    data?: object;
    isError?: boolean;
    onChange?: (file: File) => void;
    onLoad?: (response: RESPONSE_TYPE) => void;
    onError?: (response: RESPONSE_TYPE) => void;
  };

export const FileUpload = <RESPONSE_TYPE extends object>({
  className,
  url,
  name,
  data,
  disabled,
  isError,
  inputRef,
  qaId,
  header,
  uploadButtonText,
  downloadButtonText,
  description,
  allowedFileTypes,
  acceptMediaTypes,
  hasRemoveButton,
  onLoad,
  onChange,
  onDownload,
  onError,
  onTypeError,
  onReset
}: FileUploadProps<RESPONSE_TYPE>) => {
  const [isUploadLoading, setIsUploadLoading] = useState(false);
  const [fileName, setFileName] = useState<string | null>(null);

  const { mutate: uploadFile } = useUploadFile<RESPONSE_TYPE>({
    options: {
      onSuccess: (data, params) => {
        const fileName = (params.data.get(name) as File).name;
        setFileName(fileName);
        onLoad?.(data);
        setIsUploadLoading(false);
      },
      onError: (error) => {
        onError?.(error);
        setIsUploadLoading(false);
      }
    }
  });

  const handleChange = (file: File) => {
    setIsUploadLoading(true);

    onChange?.(file);

    if (!url) {
      return;
    }

    const formData = serializeFormData(data || {});

    formData.append(name, new Blob([file]), file.name);

    uploadFile({
      url,
      data: formData
    });
  };

  const handleReset = () => {
    setFileName(null);
    onReset?.();
  };

  return (
    <Tile className={classNames(css.container, className)} paddingVariant="xl">
      {isUploadLoading && (
        <div className={css.loadingOverlay}>
          <LoadingDots circlesEnabled circleSize="lg" />
        </div>
      )}
      {isError && <ErrorContent disabled={disabled} onReset={handleReset} />}
      {!fileName && !isError && (
        <EmptyContent
          inputRef={inputRef}
          name={name}
          qaId={qaId}
          disabled={disabled}
          header={header}
          description={description}
          uploadButtonText={uploadButtonText}
          downloadButtonText={downloadButtonText}
          allowedFileTypes={allowedFileTypes}
          acceptMediaTypes={acceptMediaTypes}
          onChange={handleChange}
          onTypeError={onTypeError}
          onDownload={onDownload}
        />
      )}
      {fileName && !isError && (
        <UploadedContent
          uploadedFile={fileName}
          disabled={disabled}
          hasRemoveButton={hasRemoveButton}
          onReset={handleReset}
        />
      )}
    </Tile>
  );
};
